import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { HomeContextType, ViewName } from "./HomeView.types";

import Sidebar from "../../components/SideBar/index.tsx";
import Profile from "../../components/Profile/index.tsx";
import Settings from "../../components/Settings/index.tsx";
import Projects from "../../components/Projects/index.tsx";
import NoProjects from "../../components/NoProject/index.tsx";
import NoSelectedProject from "../../components/NoSelectedProject/index.tsx";
import ProjectDetails from "../../components/ProjectDetails/index.tsx";
import CatalogAdmin from "../../components/CatalogAdmin/index.tsx";
import CatalogForbidden from "../../components/CatalogAdmin/CatalogForbidden.tsx";
import { can, DELETE_CATALOG, MANAGE_CATALOG } from "../../components/CatalogAdmin/model.tsx";
import { useLogoutModal } from "../../modals/LogoutModal/useLogoutModal.tsx";
import { useOtpPasswordModal } from "../../modals/OtpPasswordModal/useOtpPasswordModal.tsx";
import { ProfileData } from "../../components/Profile/Profile.types.tsx";
import { authResendVerificationCode } from "../../services/auth.services.tsx";
import { useChangePasswordModal } from "../../modals/ChangePasswordModal/useChangePasswordModal.tsx";
import { SettingsData } from "../../components/Settings/Settings.types.tsx";
import { useProjectDetailsOptions } from "../../modals/ProjectDetailsOptions/useProjectDetailsOptions.tsx";
import { useDeleteProjectModal } from "../../modals/DeleteProjectModal/useDeleteProjectModal.tsx";
import { useStandardProjectModal } from "../../modals/StandardProjectModal/useStandardProjectModal.tsx";
import { useNewProjectModal } from "../../modals/NewProject/useNewProjectModal.tsx";
import { useCustomDisclaimerModal } from "../../modals/CustomDisclaimerModal/useCustomDisclaimerModal.tsx";
import { useCustomProjectModal } from "../../modals/CustomProjectModal/useCustomProjectModal.tsx";
import { ProjectsData } from "../../components/Projects/Projects.types.tsx";
import { StandardFormData } from "../../modals/StandardProjectModal/StandardProjectModal.types.tsx";
import { CustomFormData } from "../../modals/CustomProjectModal/CustomProjectModal.types.tsx";
import { setAppSettings } from "../../utils/appSettings.tsx";
import { useTheme } from "../../context/Theme.context.tsx";
import {
  getProfileSettings,
  getUserProfile,
  patchProfileSettings,
} from "../../services/profile.services.tsx";
import {
  createCustomProject,
  createProject,
  deleteProject,
  getAllProjects,
  isDraft,
  getPdf,
  getProject,
  updateCustomProject,
  updateProject,
} from "../../services/project.services.tsx";
import { errorMessage } from "../../services/errorHandler.tsx";

export const HomeContext = createContext<HomeContextType | undefined>(undefined);

export const HomeContextProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { unitSystem, setUnitSystem, setThemePreference, theme } = useTheme();

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [projects, setProjects] = useState<ProjectsData | null>(null);
  const [showOnlyDrafts, setShowOnlyDrafts] = useState<boolean>(false);
  const [resetToken, setResetToken] = useState<string>("");
  const [uuid, setUuid] = useState<string>("");
  const [projectDetailsData, setProjectDetailsData] = useState<any>("");
  const [currentView, setCurrentView] = useState<ViewName>("noProjects");
  const [profileData, setProfileData] = useState<ProfileData>({
    name: "",
    company: "",
    phone: "",
    uuid: "",
    country: "",
    email: "",
    permissions: [],
  });
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);

  // Handlers
  const onLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const onOtpPassword = (token: string) => {
    setResetToken(token);
    openChangePasswordModal();
  };

  const onStandardProject = async (data: StandardFormData) => {
    try {
      setIsLoading(true);

      await createProject(data);

      await getProjects();

      setCurrentView("projects");

      toast.success("Performance created");
    } catch (e) {
      // Se relanza a proposito: el modal solo se cierra —y solo se vacia— si el calculo salio.
      // Un centrifugo rechazado por Copeland se arregla moviendo los valores, y para eso hay que
      // conservar lo que ya estaba escrito.
      toast.error(errorMessage(e, "Error creating the performance"));
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const onStandardProjectEdit = async (data: StandardFormData) => {
    try {
      setIsLoading(true);

      await updateProject(data, projectDetailsData.uuid);

      await getProjects();

      setCurrentView("projects");

      toast.success("Revision saved");
    } catch (error) {
      toast.error(errorMessage(error, "Error saving the revision"));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const onCustomProject = async (data: CustomFormData) => {
    try {
      setIsLoading(true);

      await createCustomProject(data);

      await getProjects();

      setCurrentView("projects");

      toast.success("Performance created");
    } catch (error) {
      toast.error(errorMessage(error, "Error creating the performance"));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const onCustomProjectEdit = async (data: CustomFormData) => {
    try {
      setIsLoading(true);

      await updateCustomProject(data, projectDetailsData.uuid);

      await getProjects();

      setCurrentView("projects");

      toast.success("Revision saved");
    } catch (error) {
      toast.error(errorMessage(error, "Error saving the revision"));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenOtpPasswordModal = async () => {
    try {
      openOtpPasswordModal();
      await authResendVerificationCode();
    } catch (err) {
      console.error("Error resending code:", err);
    }
  };

  const handleSubmitSettings = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();

        if (!settingsData) return;

        setIsLoading(true);

        if (!e.currentTarget.checkValidity()) return;

        const settings = await patchProfileSettings(settingsData);

        setAppSettings(settings);
        i18n.changeLanguage(settings.locale);
        setThemePreference(settings.theme);
        setUnitSystem(settings.unit_system);

        toast.success("Your changes have been saved");
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "API Error";
        toast.error(`Error: ${message}`);
      } finally {
        setIsLoading(false);
      }
    },
    [settingsData, i18n, setThemePreference, setUnitSystem]
  );

  const handleChangeSettings = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;

      setSettingsData((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          [name as keyof SettingsData]: value,
        };
      });
    },
    []
  );

  const handleDownloadPDF = async () => {
    try {
      setIsLoading(true);

      const pdfUrl = await getPdf(uuid);

      window.open(pdfUrl, "_blank", "noopener,noreferrer");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfUrl;

      document.body.appendChild(iframe);

      setTimeout(() => {
        document.body.removeChild(iframe);

        // El PDF llega como fichero, no como URL pública: la URL de objeto es de este navegador y
        // hay que soltarla o el blob se queda en memoria hasta recargar.
        URL.revokeObjectURL(pdfUrl);
      }, 3000);

      toast.success("Your PDF file has been downloaded");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteProject = async () => {
    try {
      setIsLoading(true);

      await deleteProject(uuid);

      setUuid("");

      await getProjects();

      toast.success("Performance deleted");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onProjectDetailsOptions = () => {
    const hasCustomSystem =
      !!projectDetailsData?.input_data?.custom_system &&
      Object.keys(projectDetailsData.input_data.custom_system).length > 0;

    if (hasCustomSystem) {
      openCustomProjectModalEdit();
    } else {
      openStandardProjectModalEdit();
    }

    closeProjectDetailsOptions();
  };

  const onDeleteProjectDetailsOptions = () => {
    showDeleteProjectModal();
    closeProjectDetailsOptions();
  };

  // Modals
  const { modal: logoutModal, open: openLogOutModal } = useLogoutModal({ onLogout });

  const { modal: otpPasswordModal, open: openOtpPasswordModal } = useOtpPasswordModal({
    userUuid: profileData.uuid,
    email: profileData.email,
    onOtpPassword,
  });

  const { modal: changePasswordModal, open: openChangePasswordModal } = useChangePasswordModal({
    resetToken,
  });

  const { modal: standardProjectModal, open: openStandardProjectModal } = useStandardProjectModal({
    //Todo: fix
    title: "New Performance",
    buttonLabel: "Calculate",
    onSubmit: onStandardProject,
    unitSystem: unitSystem,
  });

  const { modal: standardProjectModalEdit, open: openStandardProjectModalEdit } =
    useStandardProjectModal({
      //Todo: fix
      title: `Revise ${projectDetailsData.name}`,
      buttonLabel: "Save revision",
      initialData: projectDetailsData.input_data,
      onSubmit: onStandardProjectEdit,
      unitSystem: unitSystem,
    });

  const { modal: customProjectModal, open: openCustomProjectModal } = useCustomProjectModal({
    //Todo: fix
    title: `New Custom Performance`,
    buttonLabel: "Calculate",
    initialData: undefined,
    onSubmit: onCustomProject,
    unitSystem: unitSystem,
  });

  const { modal: customProjectModalEdit, open: openCustomProjectModalEdit } = useCustomProjectModal(
    {
      //Todo: fix
      title: `Revise ${projectDetailsData.name}`,
      buttonLabel: "Save revision",
      initialData: projectDetailsData.input_data,
      onSubmit: onCustomProjectEdit,
      unitSystem: unitSystem,
    }
  );

  const { modal: customDisclaimerModal, open: openCustomDisclaimerModal } =
    useCustomDisclaimerModal({
      onAccept: openCustomProjectModal,
    });

  const { modal: deleteProjectModal, open: showDeleteProjectModal } = useDeleteProjectModal({
    onConfirm: onDeleteProject,
  });

  const { modal: newProjectModal, open: openNewProjectModal } = useNewProjectModal({
    onStandardProject: openStandardProjectModal,
    onCustomProject: openCustomDisclaimerModal,
  });

  // Una hoja que ya adoptó un proyecto se borra desde ese proyecto, en la otra UI. Aquí ni se
  // ofrece: el backend todavía no lo impide, así que este `if` es lo único que hay.
  const canDeleteCalculation = isDraft(projectDetailsData || {});

  const {
    modal: projectDetailsModal,
    open: showProjectDetailsOptions,
    close: closeProjectDetailsOptions,
  } = useProjectDetailsOptions({
    onEdit: onProjectDetailsOptions,
    onDelete: onDeleteProjectDetailsOptions,
    canDelete: canDeleteCalculation,
  });

  const modals = [
    deleteProjectModal,
    logoutModal,
    otpPasswordModal,
    changePasswordModal,
    projectDetailsModal,
    standardProjectModal,
    standardProjectModalEdit,
    newProjectModal,
    customDisclaimerModal,
    customProjectModal,
    customProjectModalEdit,
  ];
  const spinnerVariant = theme === "dark" ? "light" : "dark";

  const spinnerLoading = (
    <Col className="d-flex justify-content-center align-items-center mt-5">
      <Spinner variant={spinnerVariant} />
    </Col>
  );

  const canManageCatalog = can(profileData.permissions, MANAGE_CATALOG);

  // Rendering
  const renderView = () => {
    if (isLoading) {
      return spinnerLoading;
    }

    switch (currentView) {
      case "noProjects":
        return <NoProjects />;

      case "projects":
        return (
          <Col className="p-0">
            <Row className="g-0">
              <Projects />
              {uuid ? <ProjectDetails /> : <NoSelectedProject />}
            </Row>
          </Col>
        );

      case "settings":
        return <Settings />;

      case "profile":
        return <Profile />;
      case "catalog":
        return canManageCatalog ? (
          <CatalogAdmin
            canDelete={can(profileData.permissions, DELETE_CATALOG)}
            onSignOut={openLogOutModal}
          />
        ) : (
          <Col className="p-0">
            <div className="m-4">
              <CatalogForbidden onSignOut={openLogOutModal} />
            </div>
          </Col>
        );
      default:
        return null;
    }
  };

  const sideBar = <Sidebar />;

  const loadingComponent = spinnerLoading;

  // get Data
  const getProfile = useCallback(async (): Promise<void> => {
    setProfileData(await getUserProfile());
  }, []);

  const getSettings = useCallback(async (): Promise<void> => {
    const { theme, unit_system, locale } = await getProfileSettings();

    setSettingsData({
      theme,
      unit_system,
      locale,
    });
  }, []);

  const getProjects = useCallback(
    async (onlyDrafts: boolean = showOnlyDrafts): Promise<void> => {
      const projects = await getAllProjects(onlyDrafts);
      if (projects) {
        setProjects(projects);
        setCurrentView("projects");
      }
    },
    [showOnlyDrafts]
  );

  // El filtro recarga en el mismo acto en vez de por efecto: el valor que hay que pedir es el que
  // acaba de elegirse, no el que el estado tendrá en la vuelta siguiente.
  const setDraftsFilter = useCallback(
    async (onlyDrafts: boolean): Promise<void> => {
      setShowOnlyDrafts(onlyDrafts);
      await getProjects(onlyDrafts);
    },
    [getProjects]
  );

  const getProjectDetails = useCallback(async (): Promise<void> => {
    try {
      if (uuid) {
        const data = await getProject(uuid);

        setProjectDetailsData(data);
      }
    } catch {
      toast.error("error");
    }
  }, [uuid]);

  // useEffects
  useEffect(() => {
    const loadSettings = async () => {
      const settings = await getProfileSettings();

      setAppSettings(settings);
      i18n.changeLanguage(settings.locale);
      setThemePreference(settings.theme);
      setUnitSystem(settings.unit_system);
    };

    loadSettings();
  }, [i18n, setThemePreference, setUnitSystem]);

  useEffect(() => {
    getProfile().catch(() => undefined);
  }, [getProfile]);

  useEffect(() => {
    if (currentView === "profile") {
      getProfile();
    }

    if (currentView === "settings") {
      getSettings();
    }

    if (currentView === "noProjects") {
      getProjects();
    }
  }, [currentView]);

  useEffect(() => {
    if (uuid) {
      getProjectDetails();
    }
  }, [uuid]);

  return (
    <HomeContext.Provider
      value={{
        handleOpenOtpPasswordModal,
        openLogOutModal,
        setCurrentView,
        modals,
        sideBar,
        renderView,
        email: profileData.email,
        profileData,
        canManageCatalog,
        isLoading,
        loadingComponent,
        settingsData,
        handleChangeSettings,
        handleSubmitSettings,
        setUuid,
        projects,
        showOnlyDrafts,
        setDraftsFilter,
        projectDetailsData,
        openStandardProjectModal,
        openNewProjectModal,
        showProjectDetailsOptions,
        handleDownloadPDF,
        showDeleteProjectModal,
        onDeleteProject,
        openStandardProjectModalEdit,
        uuid,
        openCustomDisclaimerModal,
        openCustomProjectModal,
        spinnerLoading,
        unitSystem,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};

export const useHomeContext = () => {
  const context = useContext(HomeContext);

  if (!context) {
    throw new Error("useHome must be used inside a HomeContextProvider");
  }

  return context;
};
